import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { CiSearch } from "react-icons/ci";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCopyProgramToClientsMutation,
  useLazyProgramStudentSearchQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";

/**
 * Server-side searchable multi-select for the "Copy program to clients"
 * flow. Never ships the full student list to the browser — see
 * ProgramController::studentSearch on the backend for the search endpoint.
 *
 * Pattern mirrors the assessment wizard's student picker:
 *   - debounced input hitting a paginated endpoint (limit 20)
 *   - selected students shown as removable chips above the results
 *   - the underlying results list stays scoped to what came back for the
 *     current query — nothing gets accumulated client-side beyond the chips
 */
type Picked = { id: number; name: string; reference_id?: string | null };

type ProgramMini = {
  id: number;
  programName: string;
  is_template?: boolean;
  studentId?: number | null;
};

type Props = {
  open: boolean;
  program: ProgramMini | null;
  // Batch 1A §1A.7 — bulk mode. When set (and non-empty), the dialog
  // ignores `program` and copies the same picked-student set to every
  // program in the array. Header adapts to a count. Failures per program
  // are aggregated into one toast.
  bulkPrograms?: ProgramMini[];
  onClose: () => void;
  onCopied?: (createdIds: number[]) => void;
};

export default function CopyToClientsDialog({ open, program, bulkPrograms, onClose, onCopied }: Props) {
  const isBulk = Array.isArray(bulkPrograms) && bulkPrograms.length > 0;
  const bulkCount = isBulk ? bulkPrograms!.length : 0;
  const theme = useTheme();
  const { showMessage, showErrorMessage } = useNotifier();

  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Picked[]>([]);
  const [copyToClients, { isLoading: copying }] = useCopyProgramToClientsMutation();
  const [runSearch, searchState] = useLazyProgramStudentSearchQuery();
  const debounced = useDebounce(q, 250);
  const lastQueryRef = useRef<string>("");

  // Reset picker state whenever the dialog opens for a different program.
  useEffect(() => {
    if (open) {
      setQ("");
      setPicked([]);
      lastQueryRef.current = "";
      runSearch({ q: "", limit: 20, offset: 0 });
    }
  }, [open, program?.id, runSearch]);

  // Fire the server search whenever the debounced query changes.
  useEffect(() => {
    if (!open) return;
    if (debounced === lastQueryRef.current) return;
    lastQueryRef.current = debounced;
    runSearch({ q: debounced, limit: 20, offset: 0 });
  }, [debounced, open, runSearch]);

  const results: Picked[] = useMemo(() => {
    const raw = (searchState.data as any)?.data as any[] | undefined;
    return Array.isArray(raw) ? raw : [];
  }, [searchState.data]);

  const total: number | undefined = (searchState.data as any)?.total;
  const hasMore: boolean = !!(searchState.data as any)?.hasMore;

  const togglePick = (s: Picked) => {
    setPicked((prev) =>
      prev.some((p) => p.id === s.id) ? prev.filter((p) => p.id !== s.id) : [...prev, s],
    );
  };

  const removePick = (id: number) => {
    setPicked((prev) => prev.filter((p) => p.id !== id));
  };

  const handleCopy = async () => {
    if (picked.length === 0) return;
    const studentIds = picked.map((p) => p.id);
    // Batch 1A §1A.7 — bulk mode iterates through every source program;
    // single mode preserves the original one-shot behaviour verbatim.
    if (isBulk) {
      let ok = 0, fail = 0;
      const created: number[] = [];
      for (const src of bulkPrograms!) {
        try {
          const res = await copyToClients({ programId: src.id, studentIds }).unwrap();
          if (res?.status) {
            ok++;
            const c = (res.data as any)?.createdProgramIds as number[] | undefined;
            if (Array.isArray(c)) created.push(...c);
          } else fail++;
        } catch { fail++; }
      }
      if (ok) showMessage(`${ok} program${ok === 1 ? "" : "s"} copied to ${studentIds.length} client${studentIds.length === 1 ? "" : "s"}${fail ? `, ${fail} failed` : ""}.`);
      if (fail && !ok) showErrorMessage(`${fail} cop${fail === 1 ? "y" : "ies"} failed.`);
      onCopied?.(created);
      onClose();
      return;
    }
    if (!program) return;
    try {
      const res = await copyToClients({
        programId: program.id,
        studentIds,
      }).unwrap();
      if (!res.status) {
        // Server-authored message (missing/inaccessible/already-assigned) — show verbatim.
        showErrorMessage(res.message);
        return;
      }
      showMessage(res.message); // "Grade 2 Word Problems copied to 12 clients."
      const created = (res.data as any)?.createdProgramIds as number[] | undefined;
      onCopied?.(created ?? []);
      onClose();
    } catch (e: any) {
      showErrorMessage(e?.data?.message || e?.message || "Failed to copy program.");
    }
  };

  const pickedIds = useMemo(() => new Set(picked.map((p) => p.id)), [picked]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Copy to client(s)
        </Typography>
        {isBulk ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {bulkCount} program{bulkCount === 1 ? "" : "s"} selected — each will be copied to every chosen client.
          </Typography>
        ) : program ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {program.programName}
          </Typography>
        ) : null}
      </DialogTitle>

      <DialogContent dividers>
        {/* Selected chips (removable) */}
        {picked.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              {picked.length} selected
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {picked.map((p) => (
                <Chip
                  key={p.id}
                  size="small"
                  label={p.reference_id ? `${p.name} · ${p.reference_id}` : p.name}
                  onDelete={() => removePick(p.id)}
                  deleteIcon={<CloseIcon fontSize="small" />}
                />
              ))}
            </Box>
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          autoFocus
          placeholder="Search clients by name or reference ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CiSearch size={18} />
              </InputAdornment>
            ),
            endAdornment: searchState.isFetching ? <CircularProgress size={16} /> : null,
          }}
        />

        {total !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {total === 0
              ? "No clients found."
              : hasMore
                ? `Showing first ${results.length} of ${total}. Refine your search to narrow down.`
                : `${total} match${total === 1 ? "" : "es"}.`}
          </Typography>
        )}

        <Box sx={{ mt: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, maxHeight: 320, overflow: "auto" }}>
          <List dense disablePadding>
            {results.map((s) => {
              const selected = pickedIds.has(s.id);
              return (
                <ListItemButton
                  key={s.id}
                  onClick={() => togglePick(s)}
                  selected={selected}
                  sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                >
                  <ListItemText
                    primary={s.name}
                    secondary={s.reference_id || "—"}
                    primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }}
                  />
                  {selected && (
                    <Chip size="small" color="primary" label="Selected" sx={{ ml: 1 }} />
                  )}
                </ListItemButton>
              );
            })}
            {results.length === 0 && !searchState.isFetching && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {q ? "No clients match your search." : "Type to search clients."}
                </Typography>
              </Box>
            )}
          </List>
        </Box>

        {/* Explicit info banner about the "already assigned" server-side check */}
        <Alert severity="info" sx={{ mt: 1.5 }} variant="outlined">
          If a client already has a copy of this program, the request will be rejected — no
          duplicates are created.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={copying}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          disabled={picked.length === 0 || copying || !program}
          startIcon={copying ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : undefined}
        >
          {copying
            ? "Copying…"
            : picked.length === 0
              ? "Copy"
              : `Copy to ${picked.length} client${picked.length === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
